import React, { useState, useMemo } from 'react';
import { Table, Progress, Typography } from 'antd';

const { Title } = Typography;

const VesselPerformanceDashboard = () => {
    const [hoveredChart, setHoveredChart] = useState(null);

    // Real data for vessels with 3-month values
    const realVesselData = {
        'Vessel A': {
            energy: { Oct: 1.5, Nov: 3, Dec: 22 },
            operational: { Oct: 68.2, Nov: 71.8, Dec: 74.5 },
            fuel: { Oct: 65.4, Nov: 68.9, Dec: 72.3 },
            maintenance: { Oct: 82.1, Nov: 84.7, Dec: 87.2 },
            downtime: { Oct: 88.5, Nov: 90.2, Dec: 92.8 }
        },
        'Vessel B': {
            energy: { Oct: 69.8, Nov: 73.2, Dec: 76.5 },
            operational: { Oct: 71.3, Nov: 74.9, Dec: 78.2 },
            fuel: { Oct: 63.7, Nov: 67.1, Dec: 70.8 },
            maintenance: { Oct: 79.4, Nov: 82.1, Dec: 85.6 },
            downtime: { Oct: 85.2, Nov: 87.9, Dec: 90.4 }
        },
        'Vessel C': {
            energy: { Oct: 74.2, Nov: 76.8, Dec: 79.4 },
            operational: { Oct: 66.5, Nov: 69.8, Dec: 73.1 },
            fuel: { Oct: 68.9, Nov: 72.4, Dec: 75.7 },
            maintenance: { Oct: 81.7, Nov: 84.3, Dec: 86.9 },
            downtime: { Oct: 89.1, Nov: 91.5, Dec: 93.8 }
        },
        'Vessel D': {
            energy: { Oct: 67.3, Nov: 70.9, Dec: 74.6 },
            operational: { Oct: 73.8, Nov: 76.4, Dec: 79.1 },
            fuel: { Oct: 61.2, Nov: 64.8, Dec: 68.5 },
            maintenance: { Oct: 78.5, Nov: 81.2, Dec: 84.7 },
            downtime: { Oct: 86.7, Nov: 89.3, Dec: 91.9 }
        },
        'Vessel E': {
            energy: { Oct: 71.6, Nov: 74.1, Dec: 77.8 },
            operational: { Oct: 69.4, Nov: 72.7, Dec: 76.3 },
            fuel: { Oct: 66.8, Nov: 70.2, Dec: 73.9 },
            maintenance: { Oct: 80.3, Nov: 83.6, Dec: 86.1 },
            downtime: { Oct: 87.9, Nov: 90.8, Dec: 93.2 }
        },
        'Vessel F': {
            energy: { Oct: 68.7, Nov: 72.5, Dec: 75.9 },
            operational: { Oct: 70.9, Nov: 74.2, Dec: 77.6 },
            fuel: { Oct: 64.3, Nov: 67.9, Dec: 71.4 },
            maintenance: { Oct: 77.8, Nov: 80.9, Dec: 84.2 },
            downtime: { Oct: 84.6, Nov: 88.1, Dec: 91.7 }
        }
    };

    const generateChartData = (vesselName, metric) => {
        const months = ['Oct', 'Nov', 'Dec'];
        const data = realVesselData[vesselName][metric];
        return months.map(month => ({
            value: data[month],
            month
        }));
    };

    const calculateAverage = (vesselName, metric) => {
        const data = realVesselData[vesselName][metric];
        const values = Object.values(data);
        return Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
    };

    const MiniChart = ({ data, color, id, label }) => {
        const width = 90;
        const height = 55; // Increased height for better visibility
        const padding = 8;
        const chartHeight = 40;   // Height for month labels at bottom

        const values = data.map(d => d.value);
        const max = Math.max(...values);
        const min = Math.min(...values);
        const range = max - min || 1;

        const points = values.map((value, index) => {
            const x = padding + (index * (width - padding * 2)) / (values.length - 1);
            const y = padding + (chartHeight - padding * 2) - ((value - min) / range) * (chartHeight - padding * 2);
            return `${x},${y}`;
        }).join(' ');

        return (
            <div className="rounded-3 mt-2 p-2 bg-body-tertiary"

                onMouseEnter={() => setHoveredChart(id)}
                onMouseLeave={() => setHoveredChart(null)}
            >
                <svg width={width} height={height}>
                    {/* Simple trend line connecting 3 points */}
                    <polyline
                        points={points}
                        fill="none"
                        stroke={color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />

                    {/* 3 data points with values */}
                    {values.map((value, index) => {
                        const x = padding + (index * (width - padding * 2)) / (values.length - 1);
                        const y = padding + (chartHeight - padding * 2) - ((value - min) / range) * (chartHeight - padding * 2);
                        return (
                            <g key={index}>
                                <circle
                                    cx={x}
                                    cy={y}
                                    r="2.5"
                                    fill={color}
                                    stroke="white"
                                    strokeWidth="1"
                                >
                                    <title>{`${value.toFixed(0)}% (${data[index].month})`}</title>
                                </circle>
                                {/* Small value labels */}
                                <text
                                    x={x}
                                    y={y - 2}
                                    textAnchor="middle"
                                    fontSize="7"  // Was 7
                                    fill={color}
                                    fontWeight="600"
                                >
                                    {value.toFixed(0)}
                                </text>
                            </g>
                        );
                    })}

                    {/* Month labels at the bottom */}
                    {data.map((monthData, index) => {
                        const x = padding + (index * (width - padding * 2)) / (data.length - 1);
                        return (
                            <text
                                key={index}
                                x={x}
                                y={height - 2}
                                textAnchor="middle"
                                fontSize="8"
                                fill="#666"
                                fontWeight="600"
                            >
                                {monthData.month}
                            </text>
                        );
                    })}
                </svg>


            </div>
        );
    };

    const ProgressWithTrend = ({ value, color, trendData, label, id }) => {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '90px', // Increased height to accommodate larger chart
                justifyContent: 'space-between'

            }}>
                {/* Progress bar with percentage on the right */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <Progress
                        percent={value}
                        strokeColor={color}
                        size="small"
                        showInfo={false}
                        strokeWidth={6}
                        style={{ width: '70px' }}
                    />
                    <div style={{
                        fontSize: '11px',
                        color: '#333',
                        fontWeight: 600,
                        minWidth: '30px'
                    }}>
                        {value}%
                    </div>
                </div>

                {/* Line chart at bottom */}
                <MiniChart data={trendData} color={color} id={id} label={label} />
            </div>
        );
    };

    // Generate vessel data with real values
    const vesselData = useMemo(() => [
        { key: 'A', vessel: 'Vessel A' },
        { key: 'B', vessel: 'Vessel B' },
        { key: 'C', vessel: 'Vessel C' },
        { key: 'D', vessel: 'Vessel D' },
        { key: 'E', vessel: 'Vessel E' },
        { key: 'F', vessel: 'Vessel F' }
    ].map(vessel => ({
        ...vessel,
        energy: {
            value: calculateAverage(vessel.vessel, 'energy'),
            trend: generateChartData(vessel.vessel, 'energy')
        },
        operational: {
            value: calculateAverage(vessel.vessel, 'operational'),
            trend: generateChartData(vessel.vessel, 'operational')
        },
        fuel: {
            value: calculateAverage(vessel.vessel, 'fuel'),
            trend: generateChartData(vessel.vessel, 'fuel')
        },
        maintenance: {
            value: calculateAverage(vessel.vessel, 'maintenance'),
            trend: generateChartData(vessel.vessel, 'maintenance')
        },
        downtime: {
            value: calculateAverage(vessel.vessel, 'downtime'),
            trend: generateChartData(vessel.vessel, 'downtime')
        }
    })), []);

    const columns = [
        {
            title: 'Vessel',
            dataIndex: 'vessel',
            key: 'vessel',
            width: 100,
            render: (text) => (
                <div style={{ fontWeight: 700, color: '#1890ff', fontSize: '12px' }}>
                    {text}
                </div>
            )
        },
        {
            title: (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#faad14', fontWeight: 700, fontSize: '11px' }}>Energy &</div>
                    <div style={{ color: '#faad14', fontWeight: 700, fontSize: '11px' }}>Efficiency</div>
                </div>
            ),
            dataIndex: 'energy',
            key: 'energy',
            width: 150,
            render: (data, record) => (
                <ProgressWithTrend
                    value={data.value}
                    color="#faad14"
                    trendData={data.trend}
                    label="Energy & Efficiency"
                    id={`energy-${record.key}`}
                />
            )
        },
        {
            title: (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#f5222d', fontWeight: 700, fontSize: '11px' }}>Operational</div>
                    <div style={{ color: '#f5222d', fontWeight: 700, fontSize: '11px' }}>Load</div>
                </div>
            ),
            dataIndex: 'operational',
            key: 'operational',
            width: 150,
            render: (data, record) => (
                <ProgressWithTrend
                    value={data.value}
                    color="#f5222d"
                    trendData={data.trend}
                    label="Operational Load"
                    id={`operational-${record.key}`}
                />
            )
        },
        {
            title: (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#722ed1', fontWeight: 700, fontSize: '11px' }}>Fuel &</div>
                    <div style={{ color: '#722ed1', fontWeight: 700, fontSize: '11px' }}>Lubrication</div>
                </div>
            ),
            dataIndex: 'fuel',
            key: 'fuel',
            width: 150,
            render: (data, record) => (
                <ProgressWithTrend
                    value={data.value}
                    color="#722ed1"
                    trendData={data.trend}
                    label="Fuel & Lubrication"
                    id={`fuel-${record.key}`}
                />
            )
        },
        {
            title: (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#13c2c2', fontWeight: 700, fontSize: '11px' }}>Maintenance &</div>
                    <div style={{ color: '#13c2c2', fontWeight: 700, fontSize: '11px' }}>Equipment</div>
                </div>
            ),
            dataIndex: 'maintenance',
            key: 'maintenance',
            width: 150,
            render: (data, record) => (
                <ProgressWithTrend
                    value={data.value}
                    color="#13c2c2"
                    trendData={data.trend}
                    label="Maintenance & Equipment"
                    id={`maintenance-${record.key}`}
                />
            )
        },
        {
            title: (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#52c41a', fontWeight: 700, fontSize: '11px' }}>Downtime</div>
                    <div style={{ color: '#52c41a', fontWeight: 700, fontSize: '11px' }}>Tracking</div>
                </div>
            ),
            dataIndex: 'downtime',
            key: 'downtime',
            width: 150,
            render: (data, record) => (
                <ProgressWithTrend
                    value={data.value}
                    color="#52c41a"
                    trendData={data.trend}
                    label="Downtime Tracking"
                    id={`downtime-${record.key}`}
                />
            )
        }
    ];

    return (
        <div className='w-100'>
            <Table
                columns={columns}
                dataSource={vesselData}
                pagination={false}
                size="small"
                bordered
                style={{
                    height: '100%',
                    width: '100%'
                }}
                scroll={false}
                rowClassName={(record, index) =>
                    index % 2 === 0 ? 'even-row' : 'odd-row'
                }
            />

            <div style={{
                position: 'fixed',
                bottom: '16px',
                left: '16px',
                right: '16px',
                padding: '10px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '6px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                fontSize: '10px',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                backdropFilter: 'blur(10px)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '10px', height: '3px', background: '#faad14', borderRadius: '2px' }}></div>
                    <span>Energy & Efficiency</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '10px', height: '3px', background: '#f5222d', borderRadius: '2px' }}></div>
                    <span>Operational Load</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '10px', height: '3px', background: '#722ed1', borderRadius: '2px' }}></div>
                    <span>Fuel & Lubrication</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '10px', height: '3px', background: '#13c2c2', borderRadius: '2px' }}></div>
                    <span>Maintenance</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '10px', height: '3px', background: '#52c41a', borderRadius: '2px' }}></div>
                    <span>Downtime Tracking</span>
                </div>
                <div style={{ marginLeft: 'auto', color: '#666' }}>
                    Hover charts for monthly details • 3-month trend view
                </div>
            </div>

            <style>{`
                .even-row {
                    background-color: #fafafa;
                }
                .odd-row {
                    background-color: white;
                }
                .ant-table-thead > tr > th {
                    background: #f0f2f5 !important;
                    border-bottom: 2px solid #d9d9d9 !important;
                    font-size: 10px !important;
                    padding: 8px 6px !important;
                }
                .ant-table-tbody > tr > td {
                    padding: 12px 6px !important;
                    border-bottom: 1px solid #f0f0f0 !important;
                    height: 130px !important;
                    vertical-align: middle !important;
                }
                .ant-table-tbody > tr:hover > td {
                    background: #e6f7ff !important;
                }
                .ant-progress-inner {
                    border-radius: 3px !important;
                }
                .ant-table {
                    font-size: 11px !important;
                }
                .ant-table-container {
                    height: 100% !important;
                }
                body {
                    margin: 0;
                    padding: 0;
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
};

export default VesselPerformanceDashboard;